import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
  static outlets = ["dynform--submit-stage"]
  static targets = ["ok", "log", "overlay"]
  static values = {
    enableNin: { type: Boolean, default: false },
    enableEligibility: { type: Boolean, default: false },
    isEdit: { type: Boolean, default: false }
  }

  async connect() {
    console.log("Hello from Dynform Submit Controller")
    window.submitController = this;
  }

  ninOutlet;
  uploadOutlet;
  eligibilityOutlet;
  currentSubmission;
  importId;
  okClearsForm;

  dynformSubmitStageOutletConnected(outlet, _el) {
    if (outlet.roleValue === "nin") {  this.ninOutlet = outlet }
    if (outlet.roleValue === "upload") { this.uploadOutlet = outlet }
    if (outlet.roleValue === "eligibility") { this.eligibilityOutlet = outlet }
  }

  requestHeaders() {
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    }
  }

  reset() {
    this.ninOutlet.reset()
    this.uploadOutlet.reset()
    this.eligibilityOutlet.reset()
    this.disableOk();
    this.hide();

    this.currentSubmission = null;
    this.importId = null;
    this.logTarget.textContent = null;
    this.overlayTarget.classList.remove("bg-pink/70", "bg-primary/70")
    this.overlayTarget.classList.add("bg-black/70")
    this.okTarget.textContent = "OK"
  }

  submitForm(submission) {
    this.reset()
    this.show()

    this.currentSubmission = submission;

    if (this.noCheckNin()) {
      this.ninOutlet.hide();
    }
    if (this.noCheckEligibility()) {
      this.eligibilityOutlet.hide();
    }

    this.checkNin()
  }

  shouldCheckNin() {
    return this.enableNinValue && this.currentSubmission["national"] && this.currentSubmission["id_number"]
  }

  noCheckNin() {
    return !this.shouldCheckNin()
  }

  shouldCheckEligibility() {
    return this.enableEligibilityValue
  }

  noCheckEligibility() {
    return !this.shouldCheckEligibility()
  }

  async checkNin() {
    if (this.noCheckNin()) {
      this.uploadSubmission()
      return
    }

    if (this.currentSubmission["national"] !== "National ID") {
      this.checkNinSkipped("Live verification is only for National IDs, not for " + this.currentSubmission["national"] + ". Skipping.")
      return
    }

    try {
      this.ninOutlet.start()

      let requestData = {
        "slug": this.currentSubmission["slug"],
        "nin": this.currentSubmission["id_number"]
      }

      const response = await fetch("/sources/dynamic_form_nin_verification", {
        method: "POST",
        headers: this.requestHeaders(),
        body: JSON.stringify(requestData),
      });

      let json = await response.json();
      switch (json["status"]) {
        case "valid":
          this.checkNinValid(json)
          break;
        case "invalid":
          this.checkNinInvalid()
          break;
        case "pending":
          this.checkNinSkipped("NIN could not be checked currently. And will be verified in the background later. Skipping.")
          break;
        case "disabled":
          this.checkNinSkipped("NIN check is currently not configured. Skipping.")
          break;
        default:
          this.checkNinSkipped("Unknown status: " + json["status"] + ". Check will be performed in the background later. Skipping.")
      }

    } catch (error) {
      this.checkNinError(error);
    }
  }

  checkNinValid(json) {
    this.currentSubmission["customer_id_verification_ok_h"] = json["hashed"]
    this.ninOutlet.success("NIN is valid.")
    this.uploadSubmission()
  }

  checkNinInvalid() {
    this.ninOutlet.fail("NIN is not valid. Please enter a correct NIN.")
    this.errorStyle();
    this.enableOk(false);
  }

  checkNinSkipped(message) {
    this.ninOutlet.fail(message);
    this.uploadSubmission()
  }

  checkNinError(error) {
    console.log("checkNinError")
    console.log(error)
    let msg = "Network or Server Error - failed to check the NIN";
    this.ninOutlet.retryable(msg, this.checkNin.bind(this))
  }

  async uploadSubmission() {
    console.log("uploadSubmission")
    try {
      this.uploadOutlet.start()

      const response = await fetch("/sources/dynamic_form_submit", {
        method: "POST",
        headers: this.requestHeaders(),
        body: JSON.stringify(this.currentSubmission),
      });

      let json = await response.json();

      if (response.ok) {
        this.uploadSuccess(json)
      } else {
        this.uploadFailed(json)
      }
    } catch (error) {
      this.uploadError(error);
    }
  }

  uploadSuccess(responseJson) {
    console.log("uploadSuccess")
    this.uploadOutlet.success("Upload Successful")

    this.importId = responseJson["import_id"];
    this.checkEligibility()
  }

  uploadFailed(json) {
    console.log("uploadFailed")
    console.log(json)

    this.uploadOutlet.fail(json["message"])
    this.logTarget.textContent = json["log"]
    this.errorStyle()
    this.enableOk(false)
  }

  uploadError(error) {
    console.log("uploadError")
    console.log(error)
    let msg = "Network Error - failed to upload the sale. Click Symbol to retry";
    this.uploadOutlet.retryable(msg, this.uploadSubmission.bind(this))
  }

  async checkEligibility(retry = 0) {
    if (this.noCheckEligibility()) {
      this.successStyle()
      this.enableOk(true)
      return
    }

    let maxRetries = 5;
    let timeout = retry === 0 ? 2000 : 5000;
    if (window.railsEnv === "test") {
      maxRetries = 2;
      timeout = 100;
    }

    let retriesLeft = maxRetries - retry;

    this.eligibilityOutlet.start("Waiting for eligibility result - please be patient... (" + retriesLeft + ")")
    console.log("checkEligibility")

    try {
      await new Promise(r => setTimeout(r, timeout));

      const response = await fetch("/sources/dynamic_form_eligibility?import_id=" + this.importId,
        {method: "GET", headers: this.requestHeaders()});

      let json = await response.json();

      switch (json["status"]) {
        case "eligible":
          this.eligibilitySuccess(json);
          break;
        case "ineligible":
          this.eligibilityFailed(json);
          break;
        case "pending":
          if (retriesLeft > 1) {
            console.log("Eligibility Check Pending. Retry " + (retry + 1));
            await this.checkEligibility(retry + 1);
          } else {
            this.eligibilityNoResult();
          }
          break;
        default:
          this.eligibilityError(json);
          break;
      }
    } catch (error) {
      this.eligibilityError(error);
    }
  }

  eligibilitySuccess(_responseJson) {
    this.eligibilityOutlet.success("Sale is eligible")
    this.successStyle()
    this.enableOk(true)
  }

  eligibilityFailed(responseJson) {
    let msg = responseJson["message"] + "\nPlease contact your backoffice to correct the data."
    this.eligibilityOutlet.fail(msg)
    this.errorStyle()
    this.enableOk(true)
  }

  eligibilityError(error) {
    console.log("eligibilityError")
    console.log(error)
    let msg = "Network or server error while checking the eligibility. Please retry or skip and ask the backoffice for the eligibility result"
    this.eligibilityOutlet.retryable(msg, this.checkEligibility.bind(this))
    this.errorStyle();
    this.enableOk(true)
  }

  eligibilityNoResult() {
    let msg = "Failed to get a result for the eligibility checks. You can try again by clicking the symbol. Otherwise contact the backoffice to get the eligibility status."
    this.eligibilityOutlet.retryable(msg, this.checkEligibility.bind(this))
    this.errorStyle();
    this.enableOk(true)
  }

  errorStyle() {
    this.okTarget.classList.remove("bg-primary", "hover:bg-primary/70")
    this.okTarget.classList.add("bg-pink", "hover:bg-pink/70")
    this.overlayTarget.classList.add("bg-pink/70")
    this.overlayTarget.classList.remove("bg-black/70", "bg-primary/70")
  }

  successStyle() {
    this.okTarget.classList.add("bg-primary", "hover:bg-primary/70")
    this.okTarget.classList.remove("bg-pink", "hover:bg-pink/70")
    this.overlayTarget.classList.add("bg-primary/70")
    this.overlayTarget.classList.remove("bg-black/70", "bg-pink/70")
  }

  enableOk(okClearsForm) {
    this.okClearsForm = okClearsForm;
    this.okTarget.disabled = false;
    this.okTarget.textContent = okClearsForm ? "OK" : "CANCEL"
  }

  disableOk() {
    this.okTarget.disabled = true;
  }

  show() {
    this.element.classList.remove("hidden")
  }

  hide() {
    this.element.classList.add("hidden")
  }

  ok() {
    hideConfirmation()
    if (this.okClearsForm && !this.isEditValue) {
      resetForm();
    }
    this.reset()
  }
}
